import { supabase } from './supabase'
import type {
  AddressSuggestion,
  Airport,
  AirportConfig,
  AirportConfigCreate,
  AirportConfigRuleCreate,
  AirportConfigRule,
  AirportConfigUpdate,
  TransportMode,
  TransportModeCreate,
  TransportModeUpdate,
  FlightRoute,
  FlightBatchLookupResponse,
  FlightsOverviewResponse,
  PlanFlightsGroup,
  ApiUsageSummary,
  Plan,
  PlanCreate,
  PlanStage,
  Destination,
  DestinationCreate,
  DestinationUpdate,
  Region,
  RegionCreate,
  RegionUpdate,
  ArrivalGroup,
  ArrivalGroupCreate,
  ArrivalGroupUpdate,
  Solution,
  DriverSheet,
  Matrix,
  AuditEvent,
  WorkingDraft,
  DraftUpdate,
  GroupChanges,
  PublishResponse,
  PublishedVersion,
  CoachPartner,
  CoachPartnerCreate,
  CoachPartnerUpdate,
  UserProfile,
  UserProfileCreate,
  UserProfileUpdate,
  CoachBlock,
  CoachBlockCreate,
  CoachBlockBulkCreate,
  CoachBlockUpdate,
  CoachAllocation,
  CoachAllocationCreate,
  AvailablePartner,
  PartnerAllocation,
  PartnerLink,
  DayPlanSheet,
  ReverseGeocodeResult,
} from '../types'

export type ApiOptions = { signal?: AbortSignal }

export class ApiError extends Error {
  status: number
  code: string
  details: unknown[]

  constructor(status: number, code: string, message: string, details: unknown[] = []) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

export function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Not authenticated')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

async function refreshAndGetHeaders(): Promise<Record<string, string> | null> {
  const { data, error } = await supabase.auth.refreshSession()
  if (error || !data.session) return null
  return {
    Authorization: `Bearer ${data.session.access_token}`,
    'Content-Type': 'application/json',
  }
}

function redirectToLogin(): never {
  window.location.href = '/login?expired=1'
  // Throw to halt execution — redirect is async
  throw new ApiError(401, 'SESSION_EXPIRED', 'Session expired')
}

function throwApiError(res: Response, body: Record<string, unknown>): never {
  throw new ApiError(
    res.status,
    (body.error as Record<string, unknown>)?.code as string || `HTTP_${res.status}`,
    (body.error as Record<string, unknown>)?.message as string || res.statusText,
    (body.error as Record<string, unknown>)?.details as unknown[] || [],
  )
}

async function parseErrorBody(res: Response): Promise<Record<string, unknown>> {
  return res.json().catch(() => ({ error: { message: res.statusText } }))
}

async function request<T>(method: string, path: string, body?: unknown, options?: ApiOptions): Promise<T> {
  const headers = await getAuthHeaders()
  const res = await fetch(path, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal: options?.signal,
  })
  if (res.status === 401) {
    const refreshed = await refreshAndGetHeaders()
    if (!refreshed) redirectToLogin()
    const retry = await fetch(path, {
      method,
      headers: refreshed,
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    })
    if (retry.status === 401) redirectToLogin()
    if (!retry.ok) throwApiError(retry, await parseErrorBody(retry))
    return retry.json()
  }
  if (!res.ok) throwApiError(res, await parseErrorBody(res))
  return res.json()
}

async function requestNoContent(method: string, path: string, options?: ApiOptions): Promise<void> {
  const headers = await getAuthHeaders()
  const res = await fetch(path, { method, headers, signal: options?.signal })
  if (res.status === 401) {
    const refreshed = await refreshAndGetHeaders()
    if (!refreshed) redirectToLogin()
    const retry = await fetch(path, { method, headers: refreshed, signal: options?.signal })
    if (retry.status === 401) redirectToLogin()
    if (!retry.ok) throwApiError(retry, await parseErrorBody(retry))
    return
  }
  if (!res.ok) throwApiError(res, await parseErrorBody(res))
}

export async function fetchBlob(path: string, options?: ApiOptions): Promise<Blob> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  const res = await fetch(path, {
    headers: { Authorization: `Bearer ${token}` },
    signal: options?.signal,
  })
  if (res.status === 401) {
    const refreshed = await refreshAndGetHeaders()
    if (!refreshed) redirectToLogin()
    const retry = await fetch(path, {
      headers: refreshed,
      signal: options?.signal,
    })
    if (retry.status === 401) redirectToLogin()
    if (!retry.ok) throw new ApiError(retry.status, `HTTP_${retry.status}`, 'Download failed. Please try again.')
    return retry.blob()
  }
  if (!res.ok) {
    throw new ApiError(res.status, `HTTP_${res.status}`, 'Download failed. Please try again.')
  }
  return res.blob()
}

export const api = {
  // Geocoding
  searchAddress: (q: string, lat: number, lng: number) =>
    request<AddressSuggestion[]>('GET', `/v1/geocoding/autosuggest?q=${encodeURIComponent(q)}&lat=${lat}&lng=${lng}`),

  reverseGeocode: (lat: number, lng: number) =>
    request<ReverseGeocodeResult>('GET', `/v1/geocoding/revgeocode?lat=${lat}&lng=${lng}`),

  // Flights
  lookupFlight: (flightIata: string, airportIata: string, direction: string, planDate: string) =>
    request<FlightRoute[]>('GET', `/v1/flights/lookup?flight_iata=${encodeURIComponent(flightIata)}&airport_iata=${encodeURIComponent(airportIata)}&direction=${encodeURIComponent(direction)}&plan_date=${encodeURIComponent(planDate)}`),

  lookupFlightBatch: (
    flightCodes: string[],
    airportIata: string,
    direction: string,
    planDate: string,
  ) =>
    request<FlightBatchLookupResponse>('POST', '/v1/flights/lookup-batch', {
      flight_codes: flightCodes,
      airport_iata: airportIata,
      direction,
      plan_date: planDate,
    }),

  // Airports
  searchAirports: (q: string) => request<Airport[]>('GET', `/v1/airports?q=${encodeURIComponent(q)}`),
  getAirport: (id: string) => request<Airport>('GET', `/v1/airports/${id}`),

  // Airport Configs
  getAirportConfigs: () => request<AirportConfig[]>('GET', '/v1/airport-configs'),
  getAirportConfig: (id: string) => request<AirportConfig>('GET', `/v1/airport-configs/${id}`),
  getAirportConfigByAirport: (airportId: string) =>
    request<AirportConfig | null>('GET', `/v1/airport-configs/by-airport/${airportId}`),
  createAirportConfig: (data: AirportConfigCreate) =>
    request<AirportConfig>('POST', '/v1/airport-configs', data),
  updateAirportConfig: (id: string, data: AirportConfigUpdate) =>
    request<AirportConfig>('PUT', `/v1/airport-configs/${id}`, data),
  deleteAirportConfig: (id: string) =>
    requestNoContent('DELETE', `/v1/airport-configs/${id}`),
  addAirportConfigRule: (configId: string, data: AirportConfigRuleCreate) =>
    request<AirportConfigRule>('POST', `/v1/airport-configs/${configId}/rules`, data),
  deleteAirportConfigRule: (configId: string, ruleId: string) =>
    requestNoContent('DELETE', `/v1/airport-configs/${configId}/rules/${ruleId}`),
  addTransportMode: (configId: string, data: TransportModeCreate) =>
    request<TransportMode>('POST', `/v1/airport-configs/${configId}/transport-modes`, data),
  updateTransportMode: (configId: string, modeId: string, data: TransportModeUpdate) =>
    request<TransportMode>('PUT', `/v1/airport-configs/${configId}/transport-modes/${modeId}`, data),
  deleteTransportMode: (configId: string, modeId: string) =>
    requestNoContent('DELETE', `/v1/airport-configs/${configId}/transport-modes/${modeId}`),

  // Plans
  createPlan: (data: PlanCreate) => request<Plan>('POST', '/v1/plans', data),
  getPlans: () => request<Plan[]>('GET', '/v1/plans'),
  getPlan: (id: string, options?: ApiOptions) => request<Plan>('GET', `/v1/plans/${id}`, undefined, options),
  updatePlanStage: (id: string, stage: PlanStage) =>
    request<Plan>('PATCH', `/v1/plans/${id}/stage`, { stage }),
  deletePlan: (id: string) => requestNoContent('DELETE', `/v1/plans/${id}`),

  // Versions (replaces Scenarios)
  getDraft: (planId: string, options?: ApiOptions) =>
    request<WorkingDraft>('GET', `/v1/plans/${planId}/draft`, undefined, options),
  updateDraft: (planId: string, data: DraftUpdate) =>
    request<WorkingDraft>('PUT', `/v1/plans/${planId}/draft`, data),
  resetRuleOverride: (planId: string, field: string) =>
    request<WorkingDraft>('POST', `/v1/plans/${planId}/draft/reset-rule`, { field }),
  getDraftGroupChanges: (planId: string, options?: ApiOptions) =>
    request<GroupChanges>('GET', `/v1/plans/${planId}/draft/group-changes`, undefined, options),
  publish: (planId: string) =>
    request<PublishResponse>('POST', `/v1/plans/${planId}/publish`),
  adjust: (planId: string) =>
    request<WorkingDraft>('POST', `/v1/plans/${planId}/adjust`),
  getVersions: (planId: string, options?: ApiOptions) =>
    request<PublishedVersion[]>('GET', `/v1/plans/${planId}/versions`, undefined, options),
  getVersion: (planId: string, versionNumber: number) =>
    request<PublishedVersion>('GET', `/v1/plans/${planId}/versions/${versionNumber}`),

  // Destinations
  createDestination: (data: DestinationCreate) =>
    request<Destination>('POST', '/v1/destinations', data),
  getDestinations: (regionId?: string) =>
    request<Destination[]>('GET', regionId ? `/v1/destinations?region_id=${regionId}` : '/v1/destinations'),
  updateDestination: (id: string, data: DestinationUpdate) =>
    request<Destination>('PUT', `/v1/destinations/${id}`, data),
  deleteDestination: (id: string) =>
    requestNoContent('DELETE', `/v1/destinations/${id}`),

  // Regions
  createRegion: (data: RegionCreate) =>
    request<Region>('POST', '/v1/regions', data),
  getRegions: () => request<Region[]>('GET', '/v1/regions'),
  updateRegion: (id: string, data: RegionUpdate) =>
    request<Region>('PUT', `/v1/regions/${id}`, data),
  deleteRegion: (id: string) =>
    requestNoContent('DELETE', `/v1/regions/${id}`),
  getRegionByAirport: (code: string) =>
    request<Region | null>('GET', `/v1/regions/by-airport/${code}`),

  // Arrivals (plan-scoped, works against draft)
  addArrivals: (planId: string, groups: ArrivalGroupCreate[]) =>
    request<ArrivalGroup[]>('POST', `/v1/plans/${planId}/arrivals`, groups),
  getArrivals: (planId: string, scenarioId?: string, options?: ApiOptions) =>
    request<ArrivalGroup[]>('GET', `/v1/plans/${planId}/arrivals${scenarioId ? `?scenario_id=${scenarioId}` : ''}`, undefined, options),
  updateArrival: (planId: string, arrivalId: string, data: ArrivalGroupUpdate) =>
    request<ArrivalGroup>('PUT', `/v1/plans/${planId}/arrivals/${arrivalId}`, data),
  deleteArrival: (planId: string, arrivalId: string) =>
    requestNoContent('DELETE', `/v1/plans/${planId}/arrivals/${arrivalId}`),

  // Matrix (plan-scoped)
  buildMatrix: (planId: string) =>
    request<Matrix>('POST', `/v1/plans/${planId}/matrix/build`),
  getMatrix: (planId: string, options?: ApiOptions) =>
    request<Matrix>('GET', `/v1/plans/${planId}/matrix`, undefined, options),

  // Optimization (plan-scoped)
  optimize: (planId: string) =>
    request<{ status: string; scenario_id: string }>('POST', `/v1/plans/${planId}/optimize`),
  getSolution: (planId: string, scenarioId?: string, options?: ApiOptions) =>
    request<Solution>('GET', `/v1/plans/${planId}/solution${scenarioId ? `?scenario_id=${scenarioId}` : ''}`, undefined, options),

  // Outputs (plan-scoped)
  getManifest: (planId: string, format: string = 'json', options?: ApiOptions) =>
    request<unknown>('GET', `/v1/plans/${planId}/manifest?format=${format}`, undefined, options),
  getDriverSheets: (planId: string, options?: ApiOptions) =>
    request<DriverSheet[]>('GET', `/v1/plans/${planId}/driver-sheets`, undefined, options),
  revokeDriverSheet: (planId: string, sheetId: string) =>
    request<DriverSheet>('POST', `/v1/plans/${planId}/driver-sheets/${sheetId}/revoke`),
  getPartnerLinks: (planId: string, options?: ApiOptions) =>
    request<PartnerLink[]>('GET', `/v1/plans/${planId}/partner-links`, undefined, options),
  revokePartnerLink: (planId: string, linkId: string) =>
    request<PartnerLink>('POST', `/v1/plans/${planId}/partner-links/${linkId}/revoke`),

  // Day Plan Sheet (plan-scoped)
  getDayPlanSheet: (planId: string, options?: ApiOptions) =>
    request<DayPlanSheet | null>('GET', `/v1/plans/${planId}/day-plan-sheet`, undefined, options),
  issueDayPlanSheet: (planId: string) =>
    request<DayPlanSheet>('POST', `/v1/plans/${planId}/day-plan-sheet`),
  revokeDayPlanSheet: (planId: string) =>
    request<DayPlanSheet>('DELETE', `/v1/plans/${planId}/day-plan-sheet`),

  // Audit
  getAuditEvents: (planId: string, options?: ApiOptions) =>
    request<AuditEvent[]>('GET', `/v1/plans/${planId}/audit`, undefined, options),

  // Coach Partners
  getCoachPartners: () => request<CoachPartner[]>('GET', '/v1/coach-partners'),
  getCoachPartner: (id: string) => request<CoachPartner>('GET', `/v1/coach-partners/${id}`),
  createCoachPartner: (data: CoachPartnerCreate) =>
    request<CoachPartner>('POST', '/v1/coach-partners', data),
  updateCoachPartner: (id: string, data: CoachPartnerUpdate) =>
    request<CoachPartner>('PUT', `/v1/coach-partners/${id}`, data),
  deleteCoachPartner: (id: string) =>
    requestNoContent('DELETE', `/v1/coach-partners/${id}`),
  getPartnerAllocations: (partnerId: string) =>
    request<PartnerAllocation[]>('GET', `/v1/coach-partners/${partnerId}/allocations`),
  getAvailablePartners: (regionId: string, date: string) =>
    request<AvailablePartner[]>('GET', `/v1/coach-partners/available?region_id=${regionId}&plan_date=${date}`),

  // Coach Blocks
  getCoachBlocks: (partnerId: string, startDate?: string, endDate?: string) => {
    let url = `/v1/coach-partners/${partnerId}/blocks`
    const params: string[] = []
    if (startDate) params.push(`start_date=${startDate}`)
    if (endDate) params.push(`end_date=${endDate}`)
    if (params.length) url += `?${params.join('&')}`
    return request<CoachBlock[]>('GET', url)
  },
  createCoachBlock: (partnerId: string, data: CoachBlockCreate) =>
    request<CoachBlock>('POST', `/v1/coach-partners/${partnerId}/blocks`, data),
  createCoachBlocksBulk: (partnerId: string, data: CoachBlockBulkCreate) =>
    request<CoachBlock[]>('POST', `/v1/coach-partners/${partnerId}/blocks/bulk`, data),
  updateCoachBlock: (partnerId: string, blockId: string, data: CoachBlockUpdate) =>
    request<CoachBlock>('PUT', `/v1/coach-partners/${partnerId}/blocks/${blockId}`, data),
  deleteCoachBlock: (partnerId: string, blockId: string) =>
    requestNoContent('DELETE', `/v1/coach-partners/${partnerId}/blocks/${blockId}`),

  // Coach Allocations
  getCoachAllocations: (solutionId: string, options?: ApiOptions) =>
    request<CoachAllocation[]>('GET', `/v1/coach-allocations?solution_id=${solutionId}`, undefined, options),
  createCoachAllocation: (data: CoachAllocationCreate) =>
    request<CoachAllocation>('POST', '/v1/coach-allocations', data),
  deleteCoachAllocation: (id: string) =>
    requestNoContent('DELETE', `/v1/coach-allocations/${id}`),

  // Flight Monitor
  triggerPoll: () => request<{ airports_polled: number; flights_checked: number; delays_found: number }>('POST', '/v1/flight-monitor/poll/manual'),
  getFlightsOverview: () => request<FlightsOverviewResponse>('GET', '/v1/flight-monitor/flights'),
  getPlanFlights: (planId: string, options?: ApiOptions) =>
    request<PlanFlightsGroup>('GET', `/v1/flight-monitor/plans/${planId}/flights`, undefined, options),
  getApiUsage: (days: number = 7) =>
    request<ApiUsageSummary>('GET', `/v1/flight-monitor/api-usage?days=${days}`),

  // Support
  submitFeatureRequest: (data: { title: string; category: string; description: string; priority: string }) =>
    request<{ id: string }>('POST', '/v1/support/feature-requests', data),
  submitSupportRequest: (data: { subject: string; message: string }) =>
    request<{ id: string }>('POST', '/v1/support/messages', data),

  // Route geometry
  getRouteGeometry: (waypoints: [number, number][]) => {
    const wp = waypoints.map(([lat, lon]) => `${lat},${lon}`).join('|')
    return request<{ polyline: [number, number][]; waypoints: [number, number][] }>(
      'GET',
      `/v1/route-geometry?waypoints=${encodeURIComponent(wp)}`,
    )
  },

  // User Profile
  getProfile: async (): Promise<UserProfile | null> => {
    try {
      return await request<UserProfile>('GET', '/v1/profile')
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null
      throw e
    }
  },
  createProfile: (data: UserProfileCreate) =>
    request<UserProfile>('POST', '/v1/profile', data),
  updateProfile: (data: UserProfileUpdate) =>
    request<UserProfile>('PUT', '/v1/profile', data),
}
